import { ReactNode } from "react";
import { Check } from "lucide-react";
import { BookingFormData, BookingStep, Slot } from "../../types/booking";
import { formatSlotDateLong, formatSlotTime } from "../../lib/timezone";
import { cn } from "../../lib/util";

export interface SummaryPackage {
  title: string;
  price: number;
  image?: string;
  description?: string;
}

interface BookingSummaryProps {
  pkg: SummaryPackage | null;
  pkgLoading?: boolean;
  formData: BookingFormData;
  customFacesCount: string;
  featuredImageConsent: boolean | null;
  selectedSlot: Slot | null;
  selectedDate: string;
  step: BookingStep;
}

function facesLabel(formData: BookingFormData, customFacesCount: string): string | null {
  if (!formData.numberOfFaces) return null;
  if (formData.numberOfFaces === "more") {
    const n = customFacesCount || formData.customFacesCount;
    return n ? `${n} faces` : "More than 5 faces";
  }
  const n = parseInt(formData.numberOfFaces, 10);
  return `${n} ${n === 1 ? "face" : "faces"}`;
}

function Row({
  label,
  value,
  placeholder,
}: {
  label: string;
  value?: ReactNode;
  placeholder: string;
}) {
  const filled = value !== undefined && value !== null && value !== "";
  return (
    <div className="flex items-baseline justify-between gap-4 py-2">
      <dt className="shrink-0 text-sm uppercase tracking-[0.14em] text-[#9A6B69]/80">
        {label}
      </dt>
      <dd
        className={cn(
          "text-right",
          filled ? "text-[#3a2e2c]" : "italic text-[#bcaca9]"
        )}
      >
        {filled ? value : placeholder}
      </dd>
    </div>
  );
}

export function BookingSummary({
  pkg,
  pkgLoading,
  formData,
  customFacesCount,
  featuredImageConsent,
  selectedSlot,
  selectedDate,
  step,
}: BookingSummaryProps) {
  const faces = facesLabel(formData, customFacesCount);
  const locationLabel =
    formData.location === "studio"
      ? "Studio session"
      : formData.location === "home"
      ? "Home service"
      : undefined;

  const hasDiscount = featuredImageConsent === true;
  const discounted = pkg ? Math.round(pkg.price * 0.9 * 100) / 100 : 0;

  return (
    <div className="overflow-hidden rounded-2xl border border-[#EAD7D6] bg-white">
      <div className="border-b border-[#EAD7D6] bg-[#FBEFEF]/70 px-5 py-4">
        <p className="text-xs uppercase tracking-[0.2em] text-[#B87775]">
          Your booking
        </p>
      </div>

      <div className="px-5 py-5">
        {/* Package header */}
        <div className="flex items-center gap-4">
          {pkgLoading ? (
            <div className="h-16 w-16 shrink-0 animate-pulse rounded-xl bg-[#F1DEDD]" />
          ) : pkg?.image ? (
            <img
              src={pkg.image}
              alt=""
              className="h-16 w-16 shrink-0 rounded-xl object-cover"
            />
          ) : (
            <div className="h-16 w-16 shrink-0 rounded-xl bg-[#F1DEDD]" />
          )}
          <div className="min-w-0">
            {pkgLoading ? (
              <div className="space-y-2">
                <div className="h-4 w-32 animate-pulse rounded bg-[#F1DEDD]" />
                <div className="h-3 w-20 animate-pulse rounded bg-[#F1DEDD]" />
              </div>
            ) : (
              <>
                <p className="truncate font-serif text-xl leading-tight text-[#3a2e2c]">
                  {pkg?.title ?? "Your glam package"}
                </p>
                {pkg && (
                  <p className="font-serif text-lg tabular-nums text-[#B87775]">
                    ${pkg.price}
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        <div className="my-4 h-px bg-[#EAD7D6]" />

        {/* The centrepiece: when and where */}
        <dl>
          <Row
            label="Date"
            value={selectedSlot ? formatSlotDateLong(selectedSlot.start) : undefined}
            placeholder={
              step === "schedule" ? "Choose a date" : "Set in the next step"
            }
          />
          <Row
            label="Time"
            value={
              selectedSlot ? (
                <span className="tabular-nums">
                  {formatSlotTime(selectedSlot.start)} –{" "}
                  {formatSlotTime(selectedSlot.end)}
                </span>
              ) : undefined
            }
            placeholder={
              step === "schedule" ? "Choose a time" : "Set in the next step"
            }
          />
          <Row label="Where" value={locationLabel} placeholder="Not set yet" />
          {formData.location === "home" && (
            <Row
              label="Address"
              value={formData.homeAddress}
              placeholder="Add your address"
            />
          )}
          <Row label="Party size" value={faces ?? undefined} placeholder="Not set yet" />
          <Row label="Name" value={formData.name} placeholder="Not set yet" />
        </dl>

        {/* Social-share discount, only when opted in */}
        {hasDiscount && pkg && (
          <div className="mt-4 flex items-center justify-between rounded-xl bg-[#FBEFEF] px-4 py-3">
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-[#B87775]" aria-hidden="true" />
              <span className="text-sm text-[#8a5a58]">
                Social-share discount (10%)
              </span>
            </div>
            <span className="font-serif tabular-nums text-[#8a5a58]">
              <span className="mr-1.5 text-[#bcaca9] line-through">
                ${pkg.price}
              </span>
              ${discounted}
            </span>
          </div>
        )}

        <p className="mt-4 text-xs leading-relaxed text-[#9A6B69]/80">
          Prices are a guide; the studio confirms your final total — adjusted for
          the number of faces — before any payment.
        </p>
      </div>
    </div>
  );
}
